import { z } from "zod";

// Story 11.9 AC2/AC3 — the admin create-user form contract (Screen 22).
//
// FOUR fields, and the count is a decision, not an omission. Screen 22 draws
// seven plus a note; Password (D2), Access state and Invitation-email hold (R1,
// one exception with one basis), Initial subscription (R2) and Admin note (R6)
// are all removed by PDM decision. The divergence map in the story is the W3
// expected delta — see `11-9-admin-create-user-screen.md`.
//
// `role` is a single-member literal on purpose. 11.2's request schema declares
// `role: Literal["clinician"] = Field(default="clinician")`, so the server would
// mint a clinician whether or not the client sends the key. Boundary #1 keeps the
// control anyway, with the value pinned: the client-validated screen has a Role
// field in the `cols-3` grid, and a form that shows the ceiling it can mint is
// more honest than one that hides it. This literal is what makes "the selector
// can only ever say Clinician" a compile-time fact rather than a UI convention
// (the 11.2 review's R5 handoff — resolved here as boundary #1 option (a)).

// Mirrors the backend `invite_user` schema: 255 for the names, and the RFC 5321
// ceiling of 254 for the address — the same bounds `add-person-schema.ts` pins
// against the org_admin invite, so the two invite forms refuse the same inputs.
const NAME_MAX = 255;
const EMAIL_MAX = 254;

const name = (field: string) =>
  z
    .string()
    .trim()
    .min(1, `Enter the clinician's ${field}.`)
    .max(
      NAME_MAX,
      `${field[0].toUpperCase()}${field.slice(1)} must be ${NAME_MAX} characters or fewer.`,
    );

export const CREATE_USER_ROLE = "clinician" as const;

export const createUserSchema = z.object({
  firstName: name("first name"),
  lastName: name("last name"),
  email: z
    .string()
    .trim()
    .min(1, "Enter the clinician's email address.")
    .max(EMAIL_MAX, `Email must be ${EMAIL_MAX} characters or fewer.`)
    .email("Enter a valid email address."),
  role: z.literal(CREATE_USER_ROLE),
});

export type CreateUserValues = z.infer<typeof createUserSchema>;
